import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, toolsAPI, activityAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Activity, RefreshCw, Filter, X } from 'lucide-react';

export default function ActivityTab() {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [tools, setTools] = useState([]);
  const [filters, setFilters] = useState({ user_id: '', tool_id: '', from: '', to: '' });
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    (async () => {
      try {
        const [usersRes, toolsRes] = await Promise.all([usersAPI.list(), toolsAPI.list()]);
        if (usersRes.ok) setUsers(await usersRes.clone().json());
        if (toolsRes.ok) setTools(await toolsRes.clone().json());
      } catch {}
    })();
  }, []);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: pageSize, skip: page * pageSize };
      const res = await activityAPI.list(params);
      if (res.ok) {
        const data = await res.clone().json();
        setActivities(data.items || []);
        setTotal(data.total || 0);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const clearFilters = () => {
    setFilters({ user_id: '', tool_id: '', from: '', to: '' });
    setPage(0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const hasFilters = Object.values(filters).some(v => v);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#1a2332]">Activiteitenlog</h3>
        <Button variant="outline" size="sm" onClick={loadActivity} className="rounded-xl">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Vernieuwen
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Select
          value={filters.user_id || 'all'}
          onValueChange={v => { setFilters({...filters, user_id: v === 'all' ? '' : v}); setPage(0); }}
        >
          <SelectTrigger className="w-[180px] h-9" data-testid="activity-filter-user">
            <SelectValue placeholder="Alle gebruikers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle gebruikers</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.naam}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select
          value={filters.tool_id || 'all'}
          onValueChange={v => { setFilters({...filters, tool_id: v === 'all' ? '' : v}); setPage(0); }}
        >
          <SelectTrigger className="w-[180px] h-9" data-testid="activity-filter-tool">
            <SelectValue placeholder="Alle tools" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle tools</SelectItem>
            <SelectItem value="identity">Identity</SelectItem>
            {tools.map(t => <SelectItem key={t.tool_id} value={t.tool_id}>{t.display_name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={filters.from}
          onChange={e => { setFilters({...filters, from: e.target.value ? new Date(e.target.value).toISOString() : ''}); setPage(0); }}
          className="w-[150px] h-9 text-sm"
          placeholder="Van"
          data-testid="activity-filter-date-range"
        />
        <Input
          type="date"
          value={filters.to}
          onChange={e => { setFilters({...filters, to: e.target.value ? new Date(e.target.value).toISOString() : ''}); setPage(0); }}
          className="w-[150px] h-9 text-sm"
          placeholder="Tot"
        />

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
            <X className="w-3.5 h-3.5 mr-1" /> Filters wissen
          </Button>
        )}
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Geen activiteiten gevonden</p>
              {hasFilters && (
                <Button variant="link" size="sm" onClick={clearFilters} className="mt-2 text-[#d4a853]">
                  Filters wissen
                </Button>
              )}
            </div>
          ) : (
            <Table data-testid="activity-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tijd</TableHead>
                  <TableHead>Gebruiker</TableHead>
                  <TableHead className="hidden sm:table-cell">Tool</TableHead>
                  <TableHead>Actie</TableHead>
                  <TableHead className="hidden md:table-cell">Details</TableHead>
                  <TableHead className="hidden lg:table-cell">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="tabular-nums text-xs text-slate-600 whitespace-nowrap">
                      {formatDate(a.timestamp)}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{a.user_naam || '-'}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline" className="text-xs">{a.tool_id}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-xs">{a.actie}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-500 max-w-[200px] truncate">
                      {a.details || '-'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <code className="text-xs text-slate-400">{a.ip_address || '-'}</code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-slate-500">{total} resultaten</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rounded-lg">
              Vorige
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">
              {page + 1} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="rounded-lg">
              Volgende
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
